import { CustomFontRecord, customFontOptions, isSafeFontFamily } from '@/utils/custom_fonts';

/* -------------------------------------------------------------------------
   The artboard Minyanim / Zmanim font pickers.

   `screenArtboard.minyanimFont` and `.zmanimFont` predate the design tabs and
   hold a slug ('tzlilim-pm') rather than a CSS family, so a stored value has to
   be mapped before it can be used as a `font-family`. An uploaded typeface has
   no slug — it is stored as its own family token, which `artboardFontFamily`
   passes straight through.
   ------------------------------------------------------------------------- */

interface ArtboardFontOption {
  /** Stored on the artboard. */
  value: string;
  /** Shown in the picker. */
  label: string;
  /** CSS family the slug resolves to. */
  family: string;
}

/** Faces that ship with the build, in the order the pickers offer them. */
export const BUILT_IN_ARTBOARD_FONTS: ArtboardFontOption[] = [
  { value: 'frank-ruhl-libre', label: 'Frank Ruhl Libre', family: 'FrankRuhl' },
  { value: 'tzlilim-pm', label: 'TzlilimPM', family: 'TzlilimPM' },
  { value: 'pyutim', label: 'Pyutim', family: 'Pyutim' },
  { value: 'nrkis', label: 'Nrkis', family: 'NRKis' },
  { value: 'baHaYetzira', label: 'BAHaYetzira', family: 'BAHaYetzira' },
  { value: 'buLeshem', label: 'BULeshem', family: 'BULeshem' },
  { value: 'eft-vilna', label: 'EFT Vilna', family: 'EFTVilna' },
  { value: 'eft-vilna-bold', label: 'EFT Vilna Bold', family: 'EFTVilna-Bold' },
  { value: 'fb-shefa', label: 'FB Shefa', family: 'FBShefa' },
  { value: 'fb-vilna', label: 'FB Vilna', family: 'FBVilna' },
  { value: 'fb-vilna-bold', label: 'FB Vilna Bold', family: 'FBVilna-Bold' },
  { value: 'shefa', label: 'Shefa', family: 'Shefa' },
  { value: 'shefa-bold', label: 'Shefa Bold', family: 'Shefa-Bold' },
  { value: 'shefa-classic-bold', label: 'Shefa Classic Bold', family: 'ShefaClassicBold' },
];

const FAMILY_BY_SLUG: Record<string, string> = BUILT_IN_ARTBOARD_FONTS.reduce(
  (map, option) => ({ ...map, [option.value]: option.family }),
  {} as Record<string, string>
);

/**
 * The CSS family for a stored artboard font value: a built-in slug, or the
 * family token of an uploaded face. Empty when nothing is set, so the caller's
 * inherited font is left alone.
 */
export const artboardFontFamily = (value: unknown): string => {
  const stored = String(value || '');
  if (!stored) return '';
  return FAMILY_BY_SLUG[stored] || (isSafeFontFamily(stored) ? stored : '');
};

/** Picker options: the built-in faces, then whatever the organization uploaded. */
export const artboardFontOptions = (
  fonts: CustomFontRecord[]
): Array<{ label: string; value: string }> => [
  ...BUILT_IN_ARTBOARD_FONTS.map((option) => ({ label: option.label, value: option.value })),
  ...customFontOptions(fonts),
];
